/**
 * QR 코드.
 *
 * 사이니지 기기에 주소를 입력하는 것은 고통스럽다 — TV 리모컨으로 46자짜리
 * 토큰을 치게 할 수는 없다. 태블릿·폰처럼 카메라가 있는 기기에서는 스캔이
 * 가장 빠른 길이다.
 *
 * **한 덩어리 path 로 그린다.** 모듈마다 `<rect>` 를 두면 29×29 화면에 800개가
 * 넘는 노드가 생긴다. 숫자만으로 만든 path 하나면 노드 하나다.
 */
import { useMemo, type ReactNode } from 'react';
import qrcode from 'qrcode-generator';

export function QrCode({
  value,
  size = 160,
  label,
}: {
  value: string;
  /** 그려질 픽셀 크기. 실제 모듈 수와 무관하게 이 크기에 맞춘다. */
  size?: number;
  label: string;
}): ReactNode {
  const { path, modules } = useMemo(() => {
    // 0 = 내용에 맞춰 버전 자동 선택. 'M' = 15% 복원(인쇄·화면 모두 무난).
    const qr = qrcode(0, 'M');
    qr.addData(value);
    qr.make();

    const count = qr.getModuleCount();
    let d = '';
    for (let row = 0; row < count; row++) {
      for (let col = 0; col < count; col++) {
        if (qr.isDark(row, col)) d += `M${col} ${row}h1v1h-1z`;
      }
    }
    return { path: d, modules: count };
  }, [value]);

  // 여백(quiet zone)이 없으면 스캐너가 코드 경계를 못 찾는다. 4모듈이 표준이다.
  const quiet = 4;
  const span = modules + quiet * 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${span} ${span}`}
      role="img"
      aria-label={label}
      className="shrink-0 rounded-lg bg-white p-1"
      shapeRendering="crispEdges"
    >
      <rect width={span} height={span} fill="#fff" />
      <g transform={`translate(${quiet} ${quiet})`} fill="#0f172a">
        <path d={path} />
      </g>
    </svg>
  );
}
